import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Briefcase, Calendar, CheckCircle2, Circle, Clock, Sparkles, LogOut, Brain, ArrowRight, Video, XCircle } from "lucide-react";
import Logo from "@/components/Logo";
import { toast } from "sonner";

const STAGES = [
  { key: "INVITED", label: "Invited" },
  { key: "IN_PROGRESS", label: "Assessment in progress" },
  { key: "SUBMITTED", label: "Submitted" },
  { key: "EVALUATED", label: "Under review" },
  { key: "OFFERED", label: "Offer" },
  { key: "HIRED", label: "Hired" },
];

export default function CandidateHome() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState("");
  const [withdrawCategory, setWithdrawCategory] = useState("");
  const [withdrawing, setWithdrawing] = useState(false);
  const { user, logout } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/candidate/me")
      .then(r => setData(r.data))
      .catch(() => setData({ has_application: false }))
      .finally(() => setLoading(false));
  }, []);

  const doLogout = async () => { await logout(); nav("/"); };

  const openWithdraw = () => {
    setWithdrawReason("");
    setWithdrawCategory("");
    setWithdrawOpen(true);
  };

  const confirmWithdraw = async () => {
    setWithdrawing(true);
    try {
      await api.post("/candidate/me/withdraw", {
        reason: withdrawReason.trim(),
        reason_category: withdrawCategory,
      });
      toast.success("Application withdrawn — thank you for the feedback");
      setWithdrawOpen(false);
      // Refresh
      api.get("/candidate/me").then(r => setData(r.data));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Withdraw failed");
    } finally {
      setWithdrawing(false);
    }
  };

  if (loading) return <div className="min-h-screen grid place-items-center text-neutral-500">Loading…</div>;

  if (!data?.has_application) return (
    <div className="min-h-screen bg-neutral-50 py-10 px-4" data-testid="candidate-home-empty">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><Logo variant="mark-dark" size={28} /><span className="font-bold">alfredHR</span></Link>
          <Button variant="ghost" size="sm" onClick={doLogout} data-testid="candidate-logout"><LogOut className="w-3.5 h-3.5 mr-1" /> Sign out</Button>
        </div>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-10 text-center">
            <div className="w-14 h-14 rounded-full bg-pink/10 text-pink grid place-items-center mx-auto mb-4">
              <Briefcase className="w-6 h-6" />
            </div>
            <h1 className="text-2xl font-bold font-display">No application yet</h1>
            <p className="text-neutral-600 text-sm mt-2">
              We don't have an active application under {user?.email} yet. If you've been invited to an assessment,
              check your email for the direct link.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const currentStageIdx = STAGES.findIndex(s => s.key === data.candidate.stage);

  return (
    <div className="min-h-screen bg-neutral-50 py-8 px-4" data-testid="candidate-home">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><Logo variant="mark-dark" size={28} /><span className="font-bold">alfredHR</span></Link>
          <div className="flex items-center gap-3">
            <span className="text-xs text-neutral-500 hidden sm:inline">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={doLogout} data-testid="candidate-logout"><LogOut className="w-3.5 h-3.5 mr-1" /> Sign out</Button>
          </div>
        </div>

        <Card className="border-0 shadow-sm text-white" style={{ background: "linear-gradient(135deg, #00214D 0%, #001033 100%)" }}>
          <CardContent className="p-8">
            <div className="text-[10px] uppercase tracking-widest text-pink font-semibold">Your application</div>
            <h1 className="text-3xl font-bold font-display mt-2" data-testid="candidate-job-title">{data.job.title}</h1>
            <div className="text-white/70 text-sm mt-1">{data.job.department} · {data.job.location}</div>
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <Badge className="bg-pink text-white border-0" data-testid="candidate-stage-badge">
                {STAGES.find(s => s.key === data.candidate.stage)?.label || data.candidate.stage}
              </Badge>
              <span className="text-white/60 text-xs">Applied {(data.candidate.created_at || "").slice(0, 10)}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <h2 className="font-semibold mb-4">Your progress</h2>
            <div className="space-y-3" data-testid="candidate-stage-tracker">
              {STAGES.map((s, i) => {
                const done = i < currentStageIdx;
                const current = i === currentStageIdx;
                return (
                  <div key={s.key} className="flex items-center gap-3" data-testid={`stage-${s.key}`}>
                    {done ? <CheckCircle2 className="w-5 h-5 text-teal shrink-0" />
                      : current ? <div className="w-5 h-5 rounded-full bg-pink animate-pulse shrink-0" />
                      : <Circle className="w-5 h-5 text-neutral-300 shrink-0" />}
                    <span className={`text-sm ${current ? "font-semibold text-neutral-900" : done ? "text-neutral-600" : "text-neutral-400"}`}>{s.label}</span>
                    {current && <Badge variant="outline" className="text-[10px] ml-auto">Current</Badge>}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {data.interviews?.length > 0 && (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <h2 className="font-semibold mb-4 flex items-center gap-2"><Calendar className="w-4 h-4 text-pink" /> Upcoming interviews</h2>
              <div className="space-y-2">
                {data.interviews.map(iv => (
                  <div key={iv.interview_id} className="border rounded-lg p-3 flex items-center justify-between flex-wrap gap-2" data-testid={`candidate-iv-${iv.interview_id}`}>
                    <div>
                      <div className="font-medium text-sm">{iv.title || `Round ${iv.round || 1}`}</div>
                      <div className="text-xs text-neutral-500 flex items-center gap-1 mt-0.5">
                        <Clock className="w-3 h-3" /> {(iv.scheduled_at || "").slice(0, 16).replace("T", " · ")}
                      </div>
                    </div>
                    <Badge variant="outline" className="text-[10px]">{iv.status || "Scheduled"}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {data.candidate?.invite_id && data.candidate?.video_status !== "SUBMITTED" && (data.candidate.stage === "INVITED" || data.candidate.stage === "IN_PROGRESS") && (
          <Card className="border-0 shadow-sm border-l-4 border-l-pink" data-testid="candidate-video-cta">
            <CardContent className="p-5 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-pink/10 text-pink grid place-items-center shrink-0"><Video className="w-5 h-5" /></div>
                <div>
                  <div className="font-semibold text-sm">Record your video interview</div>
                  <div className="text-xs text-neutral-500">Answer a few short questions on video · about 10 minutes</div>
                </div>
              </div>
              <Link to={`/interview/${data.candidate.invite_id}`}>
                <Button className="btn-primary rounded-full" data-testid="candidate-start-video">
                  Start recording <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {data.behavioral?.status === "INVITED" && data.behavioral?.token && (
          <Card className="border-0 shadow-sm border-l-4 border-l-pink">
            <CardContent className="p-5 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-pink/10 text-pink grid place-items-center shrink-0"><Brain className="w-5 h-5" /></div>
                <div>
                  <div className="font-semibold text-sm">Complete your behavioural check-in</div>
                  <div className="text-xs text-neutral-500">10 short questions · about 3 minutes</div>
                </div>
              </div>
              <Link to={`/behavioral/${data.behavioral.token}`}>
                <Button className="btn-primary rounded-full" data-testid="candidate-start-behavioral">
                  Start <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <h2 className="font-semibold mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4 text-pink" /> About the role</h2>
            <div className="text-sm text-neutral-700 whitespace-pre-wrap leading-relaxed max-h-[300px] overflow-y-auto">
              {data.job.description_excerpt || "—"}
            </div>
          </CardContent>
        </Card>

        {data.candidate.stage !== "WITHDRAWN" && data.candidate.stage !== "HIRED" && data.candidate.stage !== "REJECTED" && (
          <div className="text-center pt-2">
            <button onClick={openWithdraw} data-testid="candidate-withdraw-btn"
              className="text-xs text-neutral-500 hover:text-red-500 inline-flex items-center gap-1.5 transition-colors">
              <XCircle className="w-3.5 h-3.5" /> Withdraw my application
            </button>
            <div className="text-[10px] text-neutral-400 mt-1">The recruiter will be notified · your data stays with us until you request deletion.</div>
          </div>
        )}
        {data.candidate.stage === "WITHDRAWN" && (
          <Card className="border-0 shadow-sm bg-neutral-100" data-testid="candidate-withdrawn-state">
            <CardContent className="p-4 text-center text-sm text-neutral-600">
              You've withdrawn this application. Thanks for letting us know — we hope to see you again for a future role.
            </CardContent>
          </Card>
        )}

        {/* Withdraw with feedback dialog */}
        <Dialog open={withdrawOpen} onOpenChange={setWithdrawOpen}>
          <DialogContent className="max-w-md" data-testid="withdraw-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-pink" /> Withdraw application
              </DialogTitle>
              <DialogDescription className="text-xs text-neutral-500 pt-1">
                We're sorry to see you go. A quick note helps us improve the process for future candidates —
                the recruiter will only see this if you share it.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-1">
              <div>
                <Label className="text-xs">Main reason (optional)</Label>
                <div className="grid grid-cols-2 gap-1.5 mt-1.5" data-testid="withdraw-reason-chips">
                  {[
                    { key: "ACCEPTED_ELSEWHERE", label: "Accepted another offer" },
                    { key: "COMPENSATION", label: "Compensation mismatch" },
                    { key: "ROLE_FIT", label: "Role wasn't the right fit" },
                    { key: "PROCESS_LENGTH", label: "Process was too long" },
                    { key: "LOCATION", label: "Location / remote policy" },
                    { key: "OTHER", label: "Something else" },
                  ].map(opt => {
                    const active = withdrawCategory === opt.key;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => setWithdrawCategory(active ? "" : opt.key)}
                        data-testid={`withdraw-reason-${opt.key}`}
                        className={`text-xs px-2.5 py-1.5 rounded-lg border text-left transition-all ${active ? "bg-pink text-white border-pink font-semibold" : "hover:bg-neutral-50 border-neutral-200"}`}>
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-xs">Anything else you'd like the team to know? (optional)</Label>
                <Textarea
                  rows={3}
                  value={withdrawReason}
                  onChange={(e) => setWithdrawReason(e.target.value)}
                  className="mt-1.5 text-sm"
                  placeholder="Your candid feedback helps us get better…"
                  data-testid="withdraw-reason-text"
                />
              </div>
              <div className="text-[10px] text-neutral-400">This action cannot be undone from this portal.</div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setWithdrawOpen(false)} data-testid="withdraw-cancel-btn">Never mind</Button>
              <Button
                onClick={confirmWithdraw}
                disabled={withdrawing}
                data-testid="withdraw-confirm-btn"
                className="bg-red-500 hover:bg-red-600 text-white">
                {withdrawing ? "Withdrawing…" : "Withdraw application"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
